import { LearningMode } from 'app/types';

export interface ExerciseMeta {
  id: 'memorize' | 'matching' | 'missing' | 'qa';
  label: string;
  icon: 'Style' | 'SyncAlt' | 'ShortText' | 'Forum';
}

/**
 * The four exercise types a learning session walks through, in the order they
 * appear in {@code LearningSessionPage} steps. Used by the hero/mode cards and
 * the "Що буде у сесії" first-time block.
 */
export const EXERCISES: ExerciseMeta[] = [
  { id: 'memorize', label: "Запам'ятати", icon: 'Style' },
  { id: 'matching', label: 'Відповідність', icon: 'SyncAlt' },
  { id: 'missing', label: 'Пропущені слова', icon: 'ShortText' },
  { id: 'qa', label: 'Питання-відповідь', icon: 'Forum' },
];

export interface ModeMeta {
  id: LearningMode;
  label: string;
  icon:
    | 'AutoAwesome'
    | 'History'
    | 'Shuffle'
    | 'Checklist';
  desc: string;
  countLabel: string;
  minutes: number | null;
  exercises: Array<ExerciseMeta['id']>;
  actionable?: boolean;
}

export const MODES: ModeMeta[] = [
  {
    id: 'NEW_WORDS',
    label: 'Вчити нові слова',
    icon: 'AutoAwesome',
    desc: 'Свіжі слова з твого словника, які ти ще не вчив.',
    countLabel: 'нових слів',
    minutes: 8,
    exercises: ['memorize', 'matching', 'missing', 'qa'],
  },
  {
    id: 'REPEAT',
    label: 'Повторити слова',
    icon: 'History',
    desc: 'Слова, які ти вже вчив раніше — час закріпити, щоб не забути.',
    countLabel: 'чекають на повторення',
    minutes: 7,
    exercises: ['matching', 'missing', 'qa'],
  },
  {
    id: 'MIXED',
    label: 'Змішаний режим',
    icon: 'Shuffle',
    desc: 'Трохи нових і трохи на повторення — збалансована сесія.',
    countLabel: 'нові + повторення',
    minutes: 10,
    exercises: ['memorize', 'matching', 'missing', 'qa'],
  },
  {
    id: 'MANUAL',
    label: 'Обрати слова самостійно',
    icon: 'Checklist',
    desc: 'Сам познач потрібні слова у Словнику чи Моєму словнику й вчи саме їх.',
    countLabel: '',
    minutes: null,
    exercises: [],
    actionable: true,
  },
];

export const modeById = (id: LearningMode): ModeMeta | undefined =>
  MODES.find((m) => m.id === id);

export const exerciseById = (
  id: ExerciseMeta['id'],
): ExerciseMeta | undefined => EXERCISES.find((e) => e.id === id);

/**
 * Ukrainian plural for "слово": 1 слово / 2–4 слова / 5+ слів.
 */
export const slivWord = (n: number): string => {
  const d = n % 10;
  const h = n % 100;
  if (d === 1 && h !== 11) return 'слово';
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return 'слова';
  return 'слів';
};

export const fmtN = (n: number) => n.toLocaleString('uk-UA');
